import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ChevronDownIcon, UserGroupIcon, CheckIcon } from '@heroicons/react/24/outline';
import api from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

function GroupAvatar({ group, size = 'size-7' }) {
  const logoSrc = group?.logo_url ?? group?.logo;
  if (logoSrc) {
    return (
      <img
        src={logoSrc}
        alt={group.name || 'Logo'}
        className={`${size} rounded-full object-cover`}
      />
    );
  }
  const initial = group?.name ? group.name[0].toUpperCase() : '?';
  return (
    <span className={`inline-flex ${size} items-center justify-center rounded-full bg-primary-100`}>
      <span className="text-xs font-semibold text-primary-700">{initial}</span>
    </span>
  );
}

/**
 * GroupSwitcherMenuItem
 *
 * Renders a Disclosure item inside the desktop user popover for switching
 * between groups. Works for both multigroup regular users (endpoint='/auth')
 * and supervisors (endpoint='/supervisor').
 *
 * Props:
 *   currentGroupId  — the group_id currently active (from user context)
 *   onSwitch        — callback(groupId) called after a successful switch
 *   endpoint        — '/auth' (default) or '/supervisor'
 */
export default function GroupSwitcherMenuItem({ currentGroupId, onSwitch, endpoint = '/auth' }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [error, setError] = useState(null);
  const [switching, setSwitching] = useState(null);
  const mountedRef = useRef(true);
  const navigate = useNavigate();
  const { fetchMe } = useAuth();

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchGroups = async () => {
    if (fetched) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`${endpoint}/my-groups`);
      if (!mountedRef.current) return;
      setGroups(data.groups ?? []);
      setFetched(true);
    } catch {
      if (!mountedRef.current) return;
      setGroups([]);
      setError('Erreur de chargement des groupes.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleSelect = async (groupId) => {
    if (switching || String(groupId) === String(currentGroupId)) return;
    setSwitching(groupId);
    try {
      const { data } = await api.post(`${endpoint}/switch-group/${groupId}`);
      const accessToken = data.accessToken ?? data.token;
      if (accessToken) localStorage.setItem('token', accessToken);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      // Refresh user state from the new token
      await fetchMe();
      if (onSwitch) onSwitch(groupId);
      navigate('/', { replace: true });
    } catch {
      // error handled by interceptor
    } finally {
      if (mountedRef.current) setSwitching(null);
    }
  };

  const iconColor = endpoint === '/supervisor' ? 'text-purple-500' : 'text-primary-500';
  const label = endpoint === '/supervisor' ? 'Changer de groupe' : 'Changer de groupe';

  return (
    <>
      <div className="border-t border-skin" />
      <Disclosure>
        {({ open: disclosureOpen }) => (
          <>
            <DisclosureButton
              onClick={fetchGroups}
              className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-medium text-skin-secondary hover:bg-hover hover:text-skin-primary"
            >
              <span className="flex items-center gap-2">
                <UserGroupIcon className={`h-5 w-5 ${iconColor}`} />
                {label}
              </span>
              <ChevronDownIcon
                className={`h-4 w-4 text-skin-tertiary transition-transform duration-150 ${disclosureOpen ? 'rotate-180' : ''}`}
              />
            </DisclosureButton>
            <DisclosurePanel className="max-h-64 overflow-y-auto px-2 pb-2">
              {loading && (
                <div className="flex items-center justify-center py-6">
                  <svg className="h-5 w-5 animate-spin text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              )}
              {error && (
                <p className="px-3 py-3 text-center text-sm text-red-600">{error}</p>
              )}
              {!loading && !error && groups.length === 0 && (
                <p className="px-3 py-4 text-center text-sm text-skin-tertiary">Aucun groupe disponible.</p>
              )}
              {!loading && !error && groups.length > 0 && (
                <div className="flex flex-col gap-0.5">
                  {groups.map((group) => {
                    const isActive = currentGroupId != null && String(group.id) === String(currentGroupId);
                    const isSwitching = switching === group.id;
                    return (
                      <button
                        key={group.id}
                        onClick={() => handleSelect(group.id)}
                        disabled={!!switching}
                        className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                          isActive
                            ? 'bg-primary-50 ring-1 ring-primary-200'
                            : 'hover:bg-hover'
                        }`}
                      >
                        <GroupAvatar group={group} />
                        <div className="min-w-0 flex-1">
                          <p className={`truncate text-sm font-medium ${isActive ? 'text-primary-700' : 'text-skin-primary'}`}>
                            {group.name}
                          </p>
                        </div>
                        {isSwitching && (
                          <svg className="h-4 w-4 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        )}
                        {isActive && !isSwitching && (
                          <CheckIcon className="h-4 w-4 shrink-0 text-primary-600" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </>
  );
}
